import { AvailableAfe } from 'types/api-responses';
import { Link } from 'components/link';
import { formatNumber } from 'utils/number';
import styles from './edit-afe-source-modal.module.scss';

interface AfeListProps {
  /** Array of AFEs to display */
  afes: AvailableAfe[];
  /** Currently selected AFE ID */
  selectedId: string | null;
  /** Callback when an AFE is selected */
  onSelect: (id: string) => void;
}

const COST_COLUMNS = [
  { key: 'drillingCost', label: 'Drilling' },
  { key: 'completionCost', label: 'Completion' },
  { key: 'facilitiesCost', label: 'Facility' },
  { key: 'contingenciesCost', label: 'Contingency' },
  { key: 'otherCost', label: 'Other' },
  { key: 'totalCost', label: 'Total' },
] as const;

/**
 * Displays a list of AFE items with radio button selection and cost columns.
 * @return {JSX.Element} The AFE list component
 */
export const AfeList = ({ afes, selectedId, onSelect }: AfeListProps) => {
  if (afes.length === 0) {
    return (
      <div className={styles.emptyState}>
        <p>No AFEs available for this slot.</p>
      </div>
    );
  }

  return (
    <div className={styles.forecastList}>
      <div className={styles.afeHeader}>
        <span className={styles.afeHeaderLabel}>AFE</span>
        <div className={styles.costColumns}>
          {COST_COLUMNS.map((col) => (
            <span key={col.key} className={styles.costHeaderCell}>
              {col.label}
            </span>
          ))}
        </div>
      </div>
      {afes.map((afe) => {
        const isSelected = selectedId === afe.id;

        return (
          <div
            key={afe.id}
            className={`${styles.forecastItem} ${
              isSelected ? styles.selected : ''
            }`}
            onClick={() => onSelect(afe.id)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect(afe.id);
              }
            }}
          >
            <div className={styles.radioContainer}>
              <input
                type="radio"
                name="afe-selection"
                checked={isSelected}
                onChange={() => onSelect(afe.id)}
                className={styles.radioInput}
                aria-label={`Select ${afe.id}`}
              />
            </div>
            <div className={styles.afeInfo}>
              <span className={styles.forecastName}>{afe.id}</span>
              <AfeDetails afe={afe} />
            </div>
            <div className={styles.costColumns}>
              {COST_COLUMNS.map((col) => (
                <span key={col.key} className={styles.costCell}>
                  {afe[col.key] !== null && afe[col.key] !== undefined
                    ? `$${formatNumber({
                        value: afe[col.key]!,
                        minPrecision: 0,
                        maxPrecision: 0,
                        includeCommas: true,
                      })}`
                    : '--'}
                </span>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

/**
 * Renders details for an AFE item.
 * Format: "Type: {afeType} | Well: {wellId (link)}"
 * @return {JSX.Element} The AFE details component
 */
const AfeDetails = ({ afe }: { afe: AvailableAfe }) => (
  <span className={styles.detailsText}>
    {afe.afeType && (
      <>
        <span className={styles.detailLabel}>Type:</span> {afe.afeType}
      </>
    )}
    {afe.well && (
      <>
        {afe.afeType && ' | '}
        <span className={styles.detailLabel}>Well:</span>{' '}
        {afe.well.href ? (
          <Link
            to={afe.well.href}
            target="_blank"
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          >
            {afe.well.name || afe.well.id}
          </Link>
        ) : (
          afe.well.name || afe.well.id
        )}
      </>
    )}
  </span>
);
